import { ORCHESTRATION_WS_METHODS, type ServerConfig, WS_METHODS } from "@afar-research/contracts";
import * as Context from "effect/Context";
import * as Deferred from "effect/Deferred";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Schedule from "effect/Schedule";
import * as Scope from "effect/Scope";
import * as Stream from "effect/Stream";
import * as SubscriptionRef from "effect/SubscriptionRef";
import { HttpClient } from "effect/unstable/http";
import * as RpcClient from "effect/unstable/rpc/RpcClient";
import * as RpcSerialization from "effect/unstable/rpc/RpcSerialization";
import * as Socket from "effect/unstable/socket/Socket";

import { makeWsRpcProtocolClient, type WsRpcProtocolClient } from "./protocol.ts";
import type {
  ConnectionAttemptError,
  ConnectionTransientError,
  PreparedConnection,
} from "../connection/model.ts";
import { ConnectionTransientError as ConnectionTransientErrorClass } from "../connection/model.ts";
import {
  makeHostViewSynchronization,
  type SynchronizedHostViewState,
} from "../synchronization/hostView.ts";
import { makeOptimizedHostViewStream } from "../synchronization/shellBootstrap.ts";
import {
  acquireClientTransportResource,
  clientTransportEnvironmentOwner,
  ClientTransportResources,
  type ClientTransportResourceLease,
  type ClientTransportResourcesService,
} from "../connection/resources.ts";
import type { ResourceReleaseReason } from "@afar-research/shared/resourceLedger";

const SOCKET_OPEN_TIMEOUT = "15 seconds";
const OPTIMIZED_SHELL_READY_TIMEOUT = "15 seconds";

export interface RpcSession {
  readonly client: WsRpcProtocolClient;
  readonly initialConfig: Effect.Effect<ServerConfig, ConnectionAttemptError>;
  readonly hostViewChanges: Stream.Stream<SynchronizedHostViewState>;
  readonly ready: Effect.Effect<void, ConnectionAttemptError>;
  readonly probe: Effect.Effect<void, ConnectionAttemptError>;
  readonly closed: Effect.Effect<never, ConnectionTransientError>;
  readonly releaseResources?: (reason: ResourceReleaseReason) => void;
}

export class RpcSessionFactory extends Context.Service<
  RpcSessionFactory,
  {
    readonly connect: (
      connection: PreparedConnection,
    ) => Effect.Effect<RpcSession, ConnectionAttemptError, Scope.Scope>;
  }
>()("@afar-research/client-runtime/rpc/session/RpcSessionFactory") {}

type InitialConfigError = Effect.Error<
  ReturnType<WsRpcProtocolClient[typeof WS_METHODS.serverGetConfig]>
>;

function mapInitialConfigError(error: InitialConfigError): ConnectionTransientError {
  switch (error._tag) {
    case "KeybindingsConfigParseError":
    case "ServerSettingsError":
      return new ConnectionTransientErrorClass({
        reason: "host-unavailable",
        detail: error.message,
      });
    case "RpcClientError":
      return new ConnectionTransientErrorClass({
        reason: "transport",
        detail: error.message,
      });
  }
}

export const make = Effect.gen(function* () {
  const webSocketConstructor = yield* Socket.WebSocketConstructor;
  const resources = yield* ClientTransportResources;
  const httpClient = yield* HttpClient.HttpClient;

  const connect = Effect.fnUntraced(function* (connection: PreparedConnection) {
    yield* Effect.annotateCurrentSpan({
      "connection.environment.id": connection.environmentId,
    });

    const ownerIdentity = clientTransportEnvironmentOwner(connection.environmentId);
    const connectionScope = yield* Effect.scope;
    const sessionResourceLeases = new Set<ClientTransportResourceLease>();
    const sessionResources: ClientTransportResourcesService = {
      register: (registration) => {
        const lease = resources.register(registration);
        sessionResourceLeases.add(lease);
        return lease;
      },
    };
    const releaseResources = (reason: ResourceReleaseReason) => {
      for (const lease of sessionResourceLeases) {
        lease.release(reason);
      }
    };
    const websocket = yield* acquireClientTransportResource(sessionResources, {
      ownerIdentity,
      kind: "websocket",
      identity: "rpc-session",
      lifetime: "connection",
    });
    void websocket;
    const openTimeout = yield* acquireClientTransportResource(sessionResources, {
      ownerIdentity,
      kind: "timer",
      identity: "websocket-open-timeout",
      lifetime: "connection",
    });
    const connected = yield* Deferred.make<void>();
    const disconnected = yield* Deferred.make<never, ConnectionTransientError>();
    const notifyDisconnected = (error: ConnectionTransientError) =>
      Effect.sync(() => {
        // Let the RPC protocol broadcast its transport failure before the
        // supervisor closes this scope. Otherwise outstanding stream finalizers
        // each try to send an interrupt over the dead socket and teardown waits
        // for the protocol's one-second interrupt timeout.
        queueMicrotask(() => {
          Deferred.doneUnsafe(disconnected, Effect.fail(error));
        });
      });
    const hooks = RpcClient.ConnectionHooks.of({
      onConnect: Effect.sync(() => {
        openTimeout.release("close");
      }).pipe(Effect.andThen(Deferred.succeed(connected, undefined)), Effect.asVoid),
      onDisconnect: Deferred.isDone(connected).pipe(
        Effect.flatMap((wasConnected) => {
          if (!wasConnected) {
            releaseResources("failure");
          }
          return Effect.sync(() => {
            openTimeout.release("failure");
          }).pipe(
            Effect.andThen(
              notifyDisconnected(
                new ConnectionTransientErrorClass({
                  reason: "transport",
                  detail: wasConnected
                    ? `${connection.label} disconnected.`
                    : `${connection.label} could not establish a WebSocket connection.`,
                }),
              ),
            ),
          );
        }),
        Effect.asVoid,
      ),
    });
    const socketLayer = Socket.layerWebSocket(connection.socketUrl, {
      openTimeout: SOCKET_OPEN_TIMEOUT,
      protocols: connection.socketProtocols,
    }).pipe(Layer.provide(Layer.succeed(Socket.WebSocketConstructor, webSocketConstructor)));
    const protocolLayer = Layer.effect(
      RpcClient.Protocol,
      RpcClient.makeProtocolSocket({
        retryTransientErrors: false,
        retryPolicy: Schedule.recurs(0),
      }),
    ).pipe(
      Layer.provide(
        Layer.mergeAll(
          socketLayer,
          RpcSerialization.layerJson,
          Layer.succeed(RpcClient.ConnectionHooks, hooks),
        ),
      ),
    );
    const protocolContext = yield* Layer.build(protocolLayer).pipe(
      Effect.withSpan("environment.websocket.connect"),
    );
    const client = yield* makeWsRpcProtocolClient.pipe(Effect.provide(protocolContext));
    const hostViewSynchronization = yield* makeHostViewSynchronization(ownerIdentity).pipe(
      Effect.provideService(ClientTransportResources, sessionResources),
    );
    const initialConfig = yield* Effect.cached(
      client[WS_METHODS.serverGetConfig]({}).pipe(
        Effect.mapError(mapInitialConfigError),
        Effect.withSpan("environment.initialSync"),
      ),
    );
    const probe = client[WS_METHODS.serverGetConfig]({}).pipe(
      Effect.mapError(mapInitialConfigError),
      Effect.asVoid,
      Effect.withSpan("clientRuntime.connection.rpcSession.probe"),
    );
    const toConnectionError = (error: unknown): ConnectionTransientError =>
      new ConnectionTransientErrorClass({
        reason:
          typeof error === "object" &&
          error !== null &&
          "_tag" in error &&
          error._tag === "RpcClientError"
            ? "transport"
            : "host-unavailable",
        detail:
          error instanceof Error ? error.message : "Host View synchronization failed unexpectedly.",
      });
    const startLegacyHostViewAttempt = () =>
      hostViewSynchronization
        .synchronize(
          client[ORCHESTRATION_WS_METHODS.synchronizeHostView]({}).pipe(
            Stream.mapError(toConnectionError),
          ),
        )
        .pipe(Effect.provideService(Scope.Scope, connectionScope));
    const hostViewAttempt = yield* Effect.cached(
      initialConfig.pipe(
        Effect.flatMap((config) => {
          if (config.environment.capabilities.shellBootstrap !== "http-stream-v1") {
            return startLegacyHostViewAttempt();
          }
          return Effect.gen(function* () {
            const optimizedStream = yield* makeOptimizedHostViewStream({
              httpBaseUrl: connection.httpBaseUrl,
              stream: client[ORCHESTRATION_WS_METHODS.synchronizeHostViewOptimized]({}),
            }).pipe(Effect.provideService(HttpClient.HttpClient, httpClient));
            const optimizedAttempt = yield* hostViewSynchronization
              .synchronize(optimizedStream.pipe(Stream.mapError(toConnectionError)))
              .pipe(Effect.provideService(Scope.Scope, connectionScope));
            const optimizedReady = yield* Effect.exit(
              optimizedAttempt.ready.pipe(Effect.timeout(OPTIMIZED_SHELL_READY_TIMEOUT)),
            );
            if (optimizedReady._tag === "Success") return optimizedAttempt;
            return yield* startLegacyHostViewAttempt();
          });
        }),
      ),
    );
    const mappedHostViewReady = hostViewAttempt.pipe(Effect.flatMap((attempt) => attempt.ready));
    const hostViewClosed = hostViewAttempt.pipe(
      Effect.flatMap((attempt) => attempt.completed),
      Effect.andThen(
        Effect.fail(
          new ConnectionTransientErrorClass({
            reason: "host-unavailable",
            detail: "Host View synchronization ended unexpectedly.",
          }),
        ),
      ),
      Effect.catch((error) =>
        error.reason === "transport" ? Deferred.await(disconnected) : Effect.fail(error),
      ),
    );
    const closed = Deferred.await(disconnected).pipe(Effect.raceFirst(hostViewClosed));

    return {
      client,
      initialConfig,
      hostViewChanges: SubscriptionRef.changes(hostViewSynchronization.state),
      ready: Deferred.await(connected).pipe(
        Effect.andThen(
          Effect.all([initialConfig, mappedHostViewReady], { concurrency: "unbounded" }),
        ),
        Effect.asVoid,
        Effect.raceFirst(closed),
        Effect.tapError(() =>
          Effect.sync(() => {
            releaseResources("failure");
          }),
        ),
      ),
      probe,
      closed,
      releaseResources,
    } satisfies RpcSession;
  });

  return RpcSessionFactory.of({ connect });
});

export const layer = Layer.effect(RpcSessionFactory, make);
